<script setup lang="ts">
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import menu, { IMenuItem } from '@/config/menu';
import usePermissionStore from '@/store/permission';
import * as ConstantRouter from '@/constant/router';
import upgradeTip from '@/utils/upgradeTip';
import { PermissionEnum } from '@/types/permission';
import useIntro from '@/store/intro';
import SubMenu from '@/components/sub-menu/index.vue';
import { PACKAGE_EMBED_ACCESS_DESC } from '@/constant/tip';

const props = defineProps({
  collapsed: Boolean,
});
const router = useRouter();
const route = useRoute();
const intro = useIntro();
const selectedKeys = computed(() => route.matched.map(item => item.name).filter(item => !!item));
const permission = usePermissionStore();
const { hasPermission } = permission;

const matchedRouteKeys = computed(() => {
  return route.matched.map(item => item.name).filter(item => !!item) as string[];
});
const openKeys = ref([...matchedRouteKeys.value]);

onMounted(async () => {
  await nextTick();
  intro.oneStepsUpdate(0, {
    element: document.getElementById('menu-item-broadcasts'),
    title: 'Event Management',
    intro: 'Monitor and review events in the <strong><i>“Events"</i></strong>',
    position: 'right',
  });
  intro.oneStepsUpdate(3, {
    element: document.getElementById('menu-item-sources'),
    title: 'Sources',
    intro:
      'Add new sources and configure settings for hardware and software streaming methods in <strong><i>"Sources"</i></strong>.',
    position: 'right',
  });
  intro.oneStepsUpdate(4, {
    element: document.getElementById('menu-item-reports'),
    title: 'Reports',
    intro:
      'Admins and data nerds rejoice! Dive deep into viewer analytics with <strong><i>"Reports"</i></strong>.',
    position: 'right',
  });
  intro.oneStepsUpdate(5, {
    element: document.getElementById('menu-item-settings'),
    title: 'Account Settings',
    intro: 'Manage users, billing and plan in <strong><i>"Settings."</i></strong>',
    position: 'right',
  });
  intro.twoStepsUpdate(0, {
    element: document.getElementById('menu-item-embed'),
    title: 'Embed to Your Website',
    intro: 'Display events on your website with <strong><i>“Embed"</i></strong>',
    position: 'right',
  });
  intro.twoStepsUpdate(1, {
    element: document.getElementById('menu-item-media'),
    title: 'Media Managerment',
    intro: 'Manage videos, documents, images and more in <strong><i>“Media Center”</i></strong>',
    position: 'right',
  });
  intro.threeStepsUpdate(0, {
    element: document.getElementById('menu-item-sources'),
    title: 'Add a Video Source',
    intro: '',
    position: 'right',
  });
});

function isActive(menu: IMenuItem) {
  return selectedKeys.value.includes(menu.key);
}

function menuItemHasPermission(item: IMenuItem) {
  if (item.key === ConstantRouter.EMBED)
    return hasPermission(PermissionEnum.StreamToYourWebsite) && hasPermission(PermissionEnum.Embed);
  if (item.key === ConstantRouter.REPORTS) {
    if (!hasPermission(PermissionEnum.StreamToYourWebsite)) return false;
    return [
      PermissionEnum.ViewerEngagementStates,
      PermissionEnum.ViewerInsights,
      PermissionEnum.GeoAnalytics,
    ].every(item => hasPermission(item));
  }
  return true;
}

function toRoute(item: IMenuItem) {
  if (route.meta.name === item.title) {
    return;
  }
  if (!menuItemHasPermission(item)) {
    if (item.key === ConstantRouter.EMBED) {
      upgradeTip({
        desc: PACKAGE_EMBED_ACCESS_DESC,
      });
    } else {
      upgradeTip();
    }

    return;
  }
  router.push({
    name: item.key,
    params: item.initRouteParams || route.params,
  });
}
</script>

<template>
  <div class="head__logo">
    <img v-if="props.collapsed" src="@/assets/images/logo-pack-white.png" alt="logo" />
    <img v-else src="@/assets/images/logo-white.png" alt="logo" />
  </div>
  <div class="head__menu">
    <a-menu mode="inline" :selectedKeys="selectedKeys" :openKeys="openKeys">
      <template v-for="menuItem in menu" :key="menuItem.key">
        <template v-if="!menuItem.children || menuItem.hideChildrenInMenu">
          <div v-if="menuItem.splitLine" class="menu-split-line"></div>
          <a-menu-item
            :key="menuItem.key"
            @click="toRoute(menuItem)"
            :class="{
              'ant-menu-item-disabled': !menuItemHasPermission(menuItem),
            }"
            :id="'menu-item-' + menuItem.key"
          >
            <template #icon>
              <i :class="isActive(menuItem) ? menuItem.activeIcon : menuItem.icon"></i>
            </template>
            <div>
              {{ menuItem.title }}
              <img
                v-if="menuItem.key === ConstantRouter.URL_OVERLAY"
                src="@/assets/images/icon-beta.png"
                alt="icon-beta"
                class="icon-beta"
              />
              <img
                v-if="menuItem.newFeature"
                src="@/assets/images/icon-new.png"
                alt="icon-new"
                class="icon-new"
                style="margin-left: -5px"
              />
            </div>
          </a-menu-item>
        </template>
        <template v-else>
          <sub-menu
            :key="menuItem.key"
            :menu-info="menuItem"
            @menuClick="toRoute"
            :isActive="isActive"
          />
        </template>
      </template>
    </a-menu>
  </div>
</template>

<style lang="scss" scoped>
.head__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;

  img {
    height: 26px;
  }
}

.head__menu {
  :deep {
    .ant-menu {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      color: #fff;
      background: transparent !important;
      border-right: none;
    }

    .ant-menu-item {
      width: unset;
      height: auto !important;
      padding: 0 !important;
      padding: 12px !important;
      margin: 8px !important;
      line-height: unset !important;
      color: rgba(255, 255, 255, 0.8) !important;
      border-radius: 4px;

      &:active {
        background: transparent;
      }

      &:hover {
        color: #fff !important;
        background: rgba(255, 255, 255, 0.1);
      }
    }

    .ant-menu-item-icon {
      display: block;
      width: 20px;
      height: 20px;
    }

    .ant-menu-title-content {
      display: block;
    }

    .ant-menu-item-selected {
      color: #fff !important;
      background-color: #5265b3 !important;

      &::after {
        opacity: 0 !important;
      }
    }

    .ant-menu.ant-menu-inline-collapsed {
      .ant-menu-title-content {
        display: none;
        text-align: center;
      }
    }
  }

  .icon-beta {
    position: relative;
    top: -1px;
    left: 5px;
    width: 28px;
  }

  .icon-new {
    position: relative;
    top: -1px;
    left: 5px;
    width: 28px;
  }

  .menu-split-line {
    width: 140px;
    height: 1px;
    margin: 18px auto;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
  }
}

.ant-tooltip-content {
  .anticon {
    margin-right: 4px;
  }
}
</style>

<style lang="scss">
.icon-beta {
  position: relative;
  top: -1px;
  left: 5px;
  width: 28px;
  margin-right: 6px;
}
</style>
